import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import apiClient from '../../utils/apiClient';
import Tabs from '../../components/UI/Tabs';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import './BulkConfiguration.css';
import BackArrow from '../../assets/icons/arrow-left.svg';
import { clearSelectedDevices } from '../../store/devicesSlice';


const BulkConfiguration = () => {
  const navigate = useNavigate();
  const selectedDevices = useSelector((state) => state.devices.selectedDevices);
  const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const [settingsData, setSettingsData] = useState([]);
  const [activeKey, setActiveKey] = useState('');
  const [formValues, setFormValues] = useState({});
  const [editFields, setEditFields] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const { data } = await apiClient.get('/device_settings_template');
      const grouped = data.reduce((acc, tpl) => {
        acc[tpl.DeviceKind] = acc[tpl.DeviceKind] || [];
        acc[tpl.DeviceKind].push(tpl);
        return acc;
      }, {});
      const mapped = Object.entries(grouped).map(([DeviceKind, Settings]) => {
        const label = DeviceKind.replace(/\s+/g, '');
        return { label, key: label, Settings };
      });
      const norms = [...new Set(selectedDevices.map((d) => normalize(d.kind)))];
      const filtered = mapped.filter((item) =>
        norms.some((norm) => item.key.toLowerCase().includes(norm))
      );
      setSettingsData(filtered);
    })();
  }, [selectedDevices]);

  useEffect(() => {
    return () => {
      dispatch(clearSelectedDevices());
    };
  }, [dispatch]);

  useEffect(() => {
    if (settingsData.length && !settingsData.some((item) => item.key === activeKey)) {
      setActiveKey(settingsData[0].key);
    }
  }, [settingsData]);

  useEffect(() => {
    const initial = {};
    settingsData.forEach(({ key }) => {
      const names = selectedDevices
        .filter((d) => key.toLowerCase().includes(normalize(d.kind)))
        .map((d) => d.name);
      initial[key] = {
        device_label:
          names.length && names.every((n, _, arr) => n === arr[0]) ? names[0] : '',
      };
    });
    setFormValues(initial);
  }, [selectedDevices, settingsData]);

  const handleInputChange = (key, code, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [code]: value },
    }));
  };

  const toggleEditField = (key, code) => {
    setEditFields((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [code]: !prev[key]?.[code] },
    }));
  };

  const handleApply = async () => {
    const current = settingsData.find((d) => d.key === activeKey);
    const devicesToUpdate = selectedDevices.filter((d) =>
      current.key.toLowerCase().includes(normalize(d.kind))
    );
    const payload = current.Settings.map(({ Code, Name }) => ({
      Code,
      Name,
      Value: formValues[activeKey]?.[Code] ?? '',
    }));
    try {
      await Promise.all(
        devicesToUpdate.map(async (device) => {
          try {
            const checkResponse = await apiClient.get(`/device_settings/${device.deviceId}`);
            
            if (checkResponse.data && checkResponse.data.length > 0) {
              await apiClient.put(`/device_settings/${device.deviceId}`, payload);
            } else {
              await apiClient.post(`/device_settings/${device.deviceId}`, payload);
            }
          } catch (error) {
            if (error.response?.status === 404) {
              await apiClient.post(`/device_settings/${device.deviceId}`, payload);
            } else {
              throw error;
            }
          }
        })
      );
      setConfirmMessage('Settings applied successfully!');
    } 
    catch (err) {
      let msg = 'Failed to apply settings';
      if (err.response) {
        msg =
          err.response.status === 404
            ? 'Device settings endpoint not found – check server configuration'
            : `Server error: ${err.response.status} – ${err.response.statusText}`;
      }
      setConfirmMessage(msg);
    } finally {
      setShowConfirmModal(true);
    }
  };

  const goBack = () => navigate(-1);

  const renderFormFields = (settings) =>
    settings.map((setting) => {
      const { Code, Name, FieldType, Values } = setting;
      const type = FieldType || (Values ? 'Dropdown' : 'Text');
      const options = Values
        ? Values.split(',').map((v) => v.trim()).filter(Boolean)
        : [];
      if (Code === 'device_label') {
        const editing = editFields[activeKey]?.[Code];
        return (
          <div key={Code} className="form-group">
            <div className="form-label">{Name}</div>
            {editing ? (
              <input
                type="text"
                className="form-input"
                value={formValues[activeKey]?.[Code] || ''}
                onChange={(e) => handleInputChange(activeKey, Code, e.target.value)}
              />
            ) : (
              <div>{formValues[activeKey]?.[Code] || ''}</div>
            )}
            <button
              className="form-button"
              onClick={() => toggleEditField(activeKey, Code)}
            >
              {editing ? 'Save' : 'Edit'}
            </button>
          </div>
        );
      }
      switch (type) {
        case 'Text':
          return (
            <div key={Code} className="form-group">
              <div className="form-label">{Name}</div>
              <input
                type="text"
                className="form-input"
                value={formValues[activeKey]?.[Code] || ''}
                onChange={(e) => handleInputChange(activeKey, Code, e.target.value)}
              />
            </div>
          );
        case 'Checkbox':
          return (
            <div key={Code} className="form-group">
              <div className="form-label">{Name}</div>
              <div
                className={`form-checkbox ${
                  formValues[activeKey]?.[Code] ? 'checked' : ''
                }`}
                onClick={() =>
                  handleInputChange(
                    activeKey,
                    Code,
                    !formValues[activeKey]?.[Code]
                  )
                }
              >
                {formValues[activeKey]?.[Code] ? '✔' : ''}
              </div>
            </div>
          );
        case 'Dropdown':
          return (
            <div key={Code} className="form-group">
              <div className="form-label">{Name}</div>
              <select
                className="form-dropdown"
                value={formValues[activeKey]?.[Code] || ''}
                onChange={(e) =>
                  handleInputChange(activeKey, Code, e.target.value)
                }
              >
                <option value="" disabled>
                  Select an option
                </option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          );
        case 'Multi-select':
          return (
            <div key={Code} className="form-group">
              <div className="form-label">{Name}</div>
              <select
                className="form-multiselect"
                multiple
                value={formValues[activeKey]?.[Code] || []}
                onChange={(e) => {
                  const vals = Array.from(e.target.selectedOptions).map(
                    (o) => o.value
                  );
                  handleInputChange(activeKey, Code, vals);
                }}
              >
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          );
        case 'Radio':
          return (
            <div key={Code} className="form-group">
              <div className="form-label">{Name}</div>
              <div className="form-radio-group">
                {options.map((opt) => (
                  <label key={opt} className="form-radio-label">
                    <input
                      type="radio"
                      name={`${activeKey}-${Code}`}
                      value={opt}
                      checked={formValues[activeKey]?.[Code] === opt}
                      onChange={() => handleInputChange(activeKey, Code, opt)}
                    />{' '}
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          );
        default:
          return null;
      }
    });

  return (
    <div className="bulk-config-page">
      <div className="bulk-config-header">
        <button className="bulk-config-back" onClick={goBack}>
          <img src={BackArrow} alt="Back" className="back-arrow" />
        </button>
        <h1>Bulk Configuration</h1>
      </div>
      {selectedDevices.length === 0 ? (
        <p>No devices selected. Please select devices from the Devices page.</p>
      ) : settingsData.length === 0 ? (
        <p>No configuration templates available for the selected devices.</p>
      ) : (
        <>
          <Tabs
            tabs={settingsData.map((item) => item.label)}
            activeTab={activeKey}
            onTabChange={setActiveKey}
          />
          <div className="bulk-config-content">
            {settingsData
              .filter((item) => item.key === activeKey)
              .map((item) => (
                <div
                  key={item.key}
                  style={{
                    width: '50%',
                    marginRight: '2rem',
                    padding: '2rem',
                    borderRadius: '8px',
                    backgroundColor: '#F9F9F9',
                  }}
                >
                  {renderFormFields(item.Settings)}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <button className="apply-all-button" onClick={handleApply}>
                      Apply To All
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </>
      )}
      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={() => setShowConfirmModal(false)}
          message={confirmMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default BulkConfiguration;
